// Application domain models — a creator's pitch to a brand's campaign (PIT-77).
//
// Two shapes, as elsewhere in this codebase:
//   • `ApplicationRow` — the raw row as stored in / returned from Supabase.
//   • `Application`    — the app-facing shape, with the creator and campaign
//     it joins already hydrated for rendering.

import type { Creator } from './creator';
import type { CampaignRow } from './campaign';

/**
 * Every legal application status, as a runtime list so it can be checked
 * against the database rather than only asserted in a comment.
 *
 * Security-relevant: the update policies in
 * 20260812093618_applications.sql key on these values, and the
 * `applications_status_known` check constraint pins the same set. A status
 * added here without a migration is one no policy has a branch for —
 * `application.statusParity.test.ts` fails if the two drift.
 */
export const APPLICATION_STATUSES = ['submitted', 'accepted', 'declined', 'withdrawn'] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

/**
 * The status an application starts at. Pinned by the insert policy too, so a
 * client cannot apply straight into `accepted`.
 */
export const INITIAL_APPLICATION_STATUS: ApplicationStatus = 'submitted';

/** Statuses no longer open to change — the update policies refuse to touch them. */
export const TERMINAL_APPLICATION_STATUSES: readonly ApplicationStatus[] = [
  'accepted',
  'declined',
  'withdrawn',
];

/** Raw application row as stored in / returned from the `applications` table. */
export interface ApplicationRow {
  id: number;
  campaign_id: number;
  creator_id: number;
  /** The applicant's auth user id; every RLS policy scopes on this. */
  user_id: string;
  /**
   * The pitch as sent. A copy taken at submission — editing the profile pitch
   * afterwards must not rewrite what the brand received.
   */
  pitch_text: string;
  status: ApplicationStatus;
  created_at?: string;
  updated_at?: string;
  /** Present when the row is fetched with `creators(*)`. */
  creators?: Creator | null;
  /** Present when the row is fetched with `campaigns(*)`. */
  campaigns?: CampaignRow | null;
}

/**
 * An application with whichever side of it the caller needed hydrated: the
 * creator for the brand's review list, the campaign for the creator's own.
 * Either can be null when RLS could not read it — a deleted profile, or a
 * campaign whose visibility policy no longer admits the caller.
 */
export interface Application extends ApplicationRow {
  creator?: Creator | null;
  campaign?: CampaignRow | null;
}

/** Fields a creator supplies when applying; ownership and status are set by the data layer. */
export interface NewApplicationFields {
  campaign_id: number;
  pitch_text: string;
}
